import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Download, Pencil, Trash2, Lock, FileText, CheckCircle2, Upload, Loader2, BrainCircuit } from "lucide-react";
import { DOC_TYPES, MARKINGS, MARKING_LABELS, PP_HEALTH, markingClass } from "./constants";
import AskAnvilModal from "./AskAnvilModal";
import IngestBrainModal from "./IngestBrainModal";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Dir 143 — doc types whose text carries structured Brain records (past performance / personnel).
const BRAIN_DOC_TYPES = ["Past Performance Summary", "Contract Award Summary", "Key Personnel Resume"];
const isBrainDoc = (d) => (d.doc_types || []).some((t) => BRAIN_DOC_TYPES.includes(t));

export default function DocumentsTab({ onChange }) {
  const [docs, setDocs] = useState([]);
  const [modal, setModal] = useState(null); // "upload" | doc(edit)
  const [ask, setAsk] = useState(null);
  const [ingest, setIngest] = useState(null); // Dir 143 — doc being extracted into the Brain
  const [filter, setFilter] = useState("all"); // all | finalized | draft

  const load = () => api.get("/library/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const refresh = () => { load(); onChange && onChange(); };

  const shown = docs.filter((d) =>
    filter === "all" ? true : filter === "finalized" ? d.status === "Finalized" : d.status !== "Finalized");

  const del = async (d) => {
    if (!window.confirm(`Remove "${d.name}" from the library?`)) return;
    await api.delete(`/library/documents/${d.id}`); toast.success("Document removed"); refresh();
  };

  return (
    <div data-testid="documents-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1.5">
          {[["all", "All"], ["finalized", "Finalized"], ["draft", "Draft"]].map(([k, l]) => (
            <button key={k} data-testid={`docs-filter-${k}`} onClick={() => setFilter(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="add-document-btn" onClick={() => setModal("upload")}
          className="flex items-center gap-1.5 bg-zinc-900 text-white font-semibold text-sm px-3 py-2 rounded-md hover:bg-zinc-800">
          <Plus className="h-4 w-4" /> Add document
        </button>
      </div>

      <div className="space-y-2" data-testid="documents-list">
        {shown.length === 0 && <div className="text-xs text-zinc-400 py-10 text-center border border-dashed border-zinc-300 rounded-xl">No documents. Add one to feed Anvil’s knowledge base.</div>}
        {shown.map((d) => (
          <div key={d.id} data-testid={`doc-row-${d.id}`} className="bg-white border border-zinc-200 shadow-sm rounded-lg p-3 flex items-center gap-3">
            <FileText className="h-5 w-5 text-zinc-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-zinc-900 font-medium truncate">{d.name}</span>
                {d.sensitive && <Lock className="h-3.5 w-3.5 text-red-600" title="Sensitive — not in KB" />}
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${markingClass(d.marking)}`}>{d.marking || "U"}</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${d.status === "Finalized" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>{d.status}</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {(d.doc_types || []).join(", ") || "Document"}
                {d.pp_health ? ` · PP: ${d.pp_health}` : ""}
                {(d.tags || []).length ? ` · ${d.tags.map((t) => "#" + t).join(" ")}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {isBrainDoc(d) && (
                <IconBtn testid={`ingest-brain-${d.id}`} title={d.sensitive ? "Sensitive — Anvil can't read it" : "Extract Past Performance / Key Personnel into the Brain"}
                  onClick={() => setIngest(d)} disabled={d.sensitive}><BrainCircuit className="h-4 w-4 text-gold-dark" /></IconBtn>
              )}
              <IconBtn testid={`ask-anvil-${d.id}`} title={d.sensitive ? "Sensitive — Anvil can't read it" : "Ask Anvil"}
                onClick={() => setAsk(d)} disabled={d.sensitive}><AnvilIcon className="h-4 w-4" /></IconBtn>
              <a href={`${API_BASE}/library/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                data-testid={`download-doc-${d.id}`} className="p-2 rounded hover:bg-zinc-100 text-zinc-600"><Download className="h-4 w-4" /></a>
              <IconBtn testid={`edit-doc-${d.id}`} title="Edit" onClick={() => setModal(d)}><Pencil className="h-4 w-4" /></IconBtn>
              <IconBtn testid={`delete-doc-${d.id}`} title="Delete" onClick={() => del(d)}><Trash2 className="h-4 w-4 text-red-500" /></IconBtn>
            </div>
          </div>
        ))}
      </div>

      {modal === "upload" && <UploadModal onClose={() => setModal(null)} onSaved={(created) => {
        setModal(null); refresh();
        // Dir 143 — non-blocking auto-prompt: a freshly-uploaded PP/Contract-Award/Key-Personnel doc offers a
        // one-click Review so the operator never has to know the hidden button exists. Never auto-applies.
        if (created && isBrainDoc(created) && !created.sensitive) {
          toast("This looks like Past Performance — extract records into the Brain?", {
            duration: 12000,
            action: { label: "Review", onClick: () => setIngest(created) },
          });
        }
      }} />}
      {modal && modal !== "upload" && <EditModal doc={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
      {ask && <AskAnvilModal doc={ask} onClose={() => setAsk(null)} />}
      {ingest && <IngestBrainModal doc={ingest} onClose={() => setIngest(null)} onApplied={() => { refresh(); }} />}
    </div>
  );
}

const IconBtn = ({ children, onClick, disabled, title, testid }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled} title={title}
    className="p-2 rounded hover:bg-zinc-100 text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed">{children}</button>
);

function DocFields({ f, setF }) {
  const toggleType = (t) => setF({ ...f, doc_types: f.doc_types.includes(t) ? f.doc_types.filter((x) => x !== t) : [...f.doc_types, t] });
  const isPP = f.doc_types.includes("Past Performance Summary");
  return (
    <>
      <div>
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">Document type(s)</span>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {DOC_TYPES.map((t) => (
            <button key={t} type="button" data-testid={`doctype-${t}`} onClick={() => toggleType(t)}
              className={`text-[11px] px-2 py-1 rounded-full border ${f.doc_types.includes(t) ? "bg-gold-dark/15 border-gold-dark text-zinc-900" : "bg-white border-zinc-200 text-zinc-500"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">CUI marking</span>
          <select value={f.marking} onChange={(e) => setF({ ...f, marking: e.target.value })} data-testid="doc-marking"
            className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
            {MARKINGS.map((m) => <option key={m} value={m}>{MARKING_LABELS[m]}</option>)}</select></label>
        <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Status</span>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="doc-status"
            className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
            <option value="Draft">Draft (staged, not in KB)</option>
            <option value="Finalized">Finalized (feeds Anvil)</option></select></label>
      </div>
      <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Tags (comma-separated)</span>
        <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="doc-tags"
          className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none" /></label>
      {isPP && (
        <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Past performance health</span>
          <select value={f.pp_health} onChange={(e) => setF({ ...f, pp_health: e.target.value })} data-testid="doc-pp-health"
            className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
            {PP_HEALTH.map((h) => <option key={h} value={h}>{h || "—"}</option>)}</select></label>
      )}
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={f.sensitive} onChange={(e) => setF({ ...f, sensitive: e.target.checked })} data-testid="doc-sensitive" className="h-4 w-4" />
        <span className="text-sm text-zinc-700">Sensitive — encrypt at rest & keep OUT of Anvil’s knowledge base</span>
      </label>
    </>
  );
}

function UploadModal({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ doc_types: [], marking: "U", status: "Draft", tags: "", pp_health: "", sensitive: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_types", f.doc_types.join(","));
    fd.append("tags", f.tags);
    fd.append("marking", f.marking);
    fd.append("status", f.status);
    fd.append("pp_health", f.pp_health);
    fd.append("sensitive", f.sensitive);
    try { const res = await api.post("/library/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document added"); onSaved(res.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add library document" onClose={onClose} testid="upload-doc-modal">
      <label className="flex items-center gap-2 border border-dashed border-zinc-300 rounded-md px-3 py-3 cursor-pointer hover:border-gold-dark">
        <Upload className="h-4 w-4 text-zinc-500" />
        <span className="text-sm text-zinc-600 truncate">{file ? file.name : "Choose a file (.pdf .docx .xlsx .txt .md)"}</span>
        <input type="file" className="hidden" data-testid="doc-file-input" onChange={(e) => setFile(e.target.files[0])} />
      </label>
      <DocFields f={f} setF={setF} />
      <button onClick={submit} disabled={busy} data-testid="save-doc-btn"
        className="w-full mt-2 bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-60 flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : "Add to library"}</button>
    </Modal>
  );
}

function EditModal({ doc, onClose, onSaved }) {
  const [f, setF] = useState({
    doc_types: doc.doc_types || [], marking: doc.marking || "U", status: doc.status || "Draft",
    tags: (doc.tags || []).join(", "), pp_health: doc.pp_health || "", sensitive: !!doc.sensitive,
  });
  const submit = async () => {
    await api.put(`/library/documents/${doc.id}`, {
      doc_types: f.doc_types, marking: f.marking, status: f.status,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean), pp_health: f.pp_health, sensitive: f.sensitive,
    });
    toast.success("Document updated"); onSaved();
  };
  return (
    <Modal title={doc.name} onClose={onClose} testid="edit-doc-modal">
      <DocFields f={f} setF={setF} />
      <button onClick={submit} data-testid="save-doc-edit-btn"
        className="w-full mt-2 bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800 flex items-center justify-center gap-2">
        <CheckCircle2 className="h-4 w-4" /> Save</button>
    </Modal>
  );
}

const Modal = ({ title, children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
    <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-6 space-y-3 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="flex items-center justify-between"><h2 className="font-display font-bold text-lg text-zinc-900 truncate">{title}</h2><button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button></div>
      {children}
    </div>
  </div>
);
